import { useState } from 'react'

import { Button, DescriptionType, IllustrationType } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { locale } from 'modules/marketplace_modules/components/utils/locales'
import { useGetProductUpdateModules } from 'modules/product_updates/hooks/useGetProductUpdateModules'
import IntroFooter from 'modules/upsell/components/RequestUpgradeModal/_IntroFooter'
import ModalDescription from 'modules/upsell/components/RequestUpgradeModal/_ModalDescription'
import defaultIcon from 'modules/upsell/components/RequestUpgradeModal/_ModalDescription/icons/default.svg'
import icon from 'modules/upsell/components/RequestUpgradeModal/_ModalDescription/icons/rocket.svg'
import useCreateUpsellRequest, { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import type { RequestUpgradeProps } from 'modules/upsell/lib/requestUpgradeTypes'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface BaseRequestUpgradeModalProps {
  onClose: () => void
  onSuccess?: (id?: string | null) => void
  title?: string
  description?: DescriptionType
  customSuccessDescription?: string
  pageSource?: string
}

export type RequestUpgradeModalProps = BaseRequestUpgradeModalProps & RequestUpgradeProps

export type ModalConfig = {
  illustration: IllustrationType
  title: string
  isUpsellModal: boolean
}
export interface ModalDescriptionProps {
  description?: DescriptionType
  isLoading?: boolean
  notified?: boolean
  success?: boolean
}

const getDefaultMessage = (
  requestReason: ValidReasons,
  moduleRequested?: string | null
): { title: string; description: string; icon?: string } => {
  switch (requestReason) {
    case ValidReasons.Activation:
      return {
        title: i18n.t('upsell.request_modal.activation.title'),
        description: i18n.t('upsell.request_modal.activation.description'),
        icon: icon,
      }
    case ValidReasons.MissingAddon:
      return {
        title: i18n.t('upsell.request_modal.missing_module.title', {
          module_name: moduleRequested,
        }),
        description: i18n.t('upsell.request_modal.missing_module.description'),
      }
    case ValidReasons.MissingFeature:
      return {
        title: i18n.t('upsell.request_modal.missing_feature.title'),
        description: i18n.t('upsell.request_modal.missing_feature.description'),
        icon: icon,
      }
    case ValidReasons.MissingFeatureLegacy:
      return {
        title: i18n.t('upsell.request_modal.missing_feature.title'),
        description: i18n.t('upsell.request_modal.missing_feature.description'),
        icon: icon,
      }
    case ValidReasons.UpgradeFromLegacy:
      return {
        title: i18n.t('upsell.request_modal.legacy_plan.title'),
        description: i18n.t('upsell.request_modal.legacy_plan.description'),
        icon: icon,
      }
  }
}

const RequestUpgradeModal = ({
  title,
  description,
  customSuccessDescription,
  onClose,
  onSuccess,
  requestSource,
  requestReason,
  moduleRequested,
  featureRequested,
  pageSource,
}: RequestUpgradeModalProps) => {
  const [showModal, setShowModal] = useState(true)
  const [datoError] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [isCalendarAvailable, setIsCalendarAvailable] = useState(false)
  const [customDescription] = useState(description)
  const { create, loading, called } = useCreateUpsellRequest()

  const hubspotModuleName =
    moduleRequested === AvailableModules.marketplace ? 'Integrations' : moduleRequested
  const legacyModuleName =
    requestReason === ValidReasons.UpgradeFromLegacy ? 'Core' : moduleRequested
  const notified = submitSuccess || called

  const upsellRequestSourceCategory =
    moduleRequested === AvailableModules.marketplace ? 'Integrations' : ''
  const upsellRequestType = moduleRequested === AvailableModules.marketplace ? 'Integration' : ''

  const session = useCurrentSession()
  const datoFormatLocale = locale[session.access.locale]
  const { modules, loading: loadingModule } = useGetProductUpdateModules({
    moduleSlug: moduleRequested,
    locale: datoFormatLocale,
  })
  const [{ name: moduleRequestedName = '', icon: moduleIcon = '' } = {}] = modules
  const defaultMessage = getDefaultMessage(requestReason, moduleRequestedName)

  const onRequestContact = async () => {
    setSubmitSuccess(false)

    const result = await create({
      moduleRequested,
      requestSource,
      requestReason,
      featureRequested,
      pageSource,
      hubspotModuleName: hubspotModuleName || legacyModuleName,
      requestSourceCategory: upsellRequestSourceCategory,
      upsellRequestType: upsellRequestType,
      requestSourceComponent: 'Old Modal',
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      setSubmitSuccess(true)
      setIsCalendarAvailable(false)
      onSuccess?.(handled.data?.upsellRequest?.id)
    }
  }

  const customOnClose = () => {
    onClose()
    setShowModal(false)
    setIsCalendarAvailable(false)
  }

  const getModalConfig = (): ModalConfig => {
    const result: ModalConfig = {
      illustration: 'crown',
      title: '',
      isUpsellModal: true,
    }

    if (!loading && notified) {
      result.illustration = submitSuccess ? 'crown' : 'delete-cross'
      result.title = submitSuccess
        ? i18n.t('upsell.request_modal.success_title')
        : i18n.t('packaging.upgrade_modal.retry_title')
    }

    return result
  }

  const getModalFooter = () => {
    if (isCalendarAvailable) {
      return (
        <Button onClick={() => setIsCalendarAvailable(false)} type="secondary">
          {i18n.t('upsell.go_back')}
        </Button>
      )
    } else if (notified) {
      return (
        <Button
          onClick={submitSuccess ? onClose : onRequestContact}
          type="secondary"
          disabled={loading}
        >
          {submitSuccess
            ? i18n.t('upsell.request_modal.success_button')
            : i18n.t('common.try_again')}
        </Button>
      )
    } else {
      return (
        <IntroFooter
          isLoading={loading}
          onRequestContact={onRequestContact}
          setIsCalendarAvailable={setIsCalendarAvailable}
        />
      )
    }
  }

  if (loadingModule) return null

  if (!showModal) return null
  if (moduleRequested && !featureRequested && !datoError) {
    return (
      <UpsellingKit.ProductModal
        moduleRequested={moduleRequested}
        requestSource={requestSource ?? ''}
        requestReason={requestReason}
        pageSource={pageSource ?? requestSource ?? ''}
        isOpen
        onClose={() => {
          onClose()
          onSuccess?.()
        }}
        openFromUpsellButton={false}
      />
    )
  }

  return (
    <Modal
      background="radial-gradient(50% 85.16% at 50% 0%, #FBE3BC 0%, #FFFFFF 100%)"
      {...getModalConfig()}
      description={
        <ModalDescription
          title={title || defaultMessage.title}
          customSuccessDescription={customSuccessDescription}
          description={customDescription || defaultMessage.description}
          icon={defaultMessage.icon || moduleIcon || defaultIcon}
          isLoading={loading}
          success={submitSuccess}
        />
      }
      footer={getModalFooter()}
      onClose={customOnClose}
      locked={loading}
    ></Modal>
  )
}

export default observer(RequestUpgradeModal)
